// components/Pages/MeasurementRequests/MeasurementRequests.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchMeasurementRequests, deleteMeasurementRequest } from "../../../utils/redux/measurementRequest/measurementActions";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { IoIosArrowDown } from "react-icons/io";
import { formatKey } from "../../../helpers/formatKey";

const MeasurementRequestsPage = () => {
    const dispatch = useDispatch();
    const { requests, isLoading } = useSelector(state => state.measurementRequest);

    const [filters, setFilters] = useState({
        status: "",
        request_type: ""
    });

    const [openRow, setOpenRow] = useState(null);

    useEffect(() => {
        dispatch(fetchMeasurementRequests(1, 50, filters));
    }, [filters, dispatch]);

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this measurement request?")) return;
        const res = await dispatch(deleteMeasurementRequest(id));
        if (res?.success) toast.success("Deleted successfully");
    };

    const getStatusBadge = (status) => {
        const badges = {
            'PENDING': { class: 'badge bg-warning text-dark', text: 'Pending' },
            'SCHEDULED': { class: 'badge bg-info', text: 'Scheduled' },
            'COMPLETED': { class: 'badge bg-success', text: 'Completed' },
            'CANCELLED': { class: 'badge bg-danger', text: 'Cancelled' }
        };
        return badges[status] || { class: 'badge bg-secondary', text: status };
    };

    const getRequestTypeBadge = (type) => {
        return type === 'MEASUREMENT'
            ? { class: 'badge bg-primary', text: 'Measurement' }
            : { class: 'badge bg-secondary', text: 'Appointment' };
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getTimeSlotLabel = (slot) => {
        const slots = {
            'MORNING': 'Morning (9 AM - 12 PM)',
            'AFTERNOON': 'Afternoon (12 PM - 4 PM)',
            'EVENING': 'Evening (4 PM - 7 PM)'
        };
        return slots[slot] || slot || 'N/A';
    };

    if (isLoading && requests.length === 0) {
        return (
            <div className="container py-5 text-center">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading measurement requests...</p>
            </div>
        );
    }

    return (
        <div className="container-fluid px-4">
            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center my-4">
                <div>
                    <h3>Measurement & Appointment Requests</h3>
                    <p className="text-muted mb-0">Manage all measurement and appointment requests from users</p>
                </div>
            </div>

            {/* FILTERS */}
            <div className="row mb-4">
                <div className="col-md-3">
                    <label className="form-label small text-muted">Status</label>
                    <select
                        className="form-select"
                        value={filters.status}
                        onChange={e =>
                            setFilters(prev => ({
                                ...prev,
                                status: e.target.value
                            }))
                        }
                    >
                        <option value="">All Status</option>
                        <option value="PENDING">Pending</option>
                        <option value="SCHEDULED">Scheduled</option>
                        <option value="COMPLETED">Completed</option>
                        <option value="CANCELLED">Cancelled</option>
                    </select>
                </div>

                <div className="col-md-3">
                    <label className="form-label small text-muted">Request Type</label>
                    <select
                        className="form-select"
                        value={filters.request_type}
                        onChange={e =>
                            setFilters(prev => ({
                                ...prev,
                                request_type: e.target.value
                            }))
                        }
                    >
                        <option value="">All Types</option>
                        <option value="MEASUREMENT">Measurement</option>
                        <option value="APPOINTMENT">Appointment</option>
                    </select>
                </div>
            </div>

            {/* TABLE */}
            <div className="table-responsive">
                <table className="table table-sm table-hover align-middle">
                    <thead className="table-secondary">
                        <tr>
                            <th style={{ width: "40px" }}></th>
                            <th>ID</th>
                            <th>Type</th>
                            <th>Requested By</th>
                            <th>Client</th>
                            <th>Preferred Date</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {requests?.length ? (
                            requests.map((request, index) => {
                                const statusBadge = getStatusBadge(request.status);
                                const typeBadge = getRequestTypeBadge(request.request_type);
                                const isOpen = openRow === index;

                                return (
                                    <React.Fragment key={request._id}>
                                        {/* MAIN ROW */}
                                        <tr
                                            style={{ cursor: "pointer" }}
                                            onClick={() => setOpenRow(isOpen ? null : index)}
                                        >
                                            <td>
                                                <span
                                                    style={{
                                                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                                                        display: "inline-block",
                                                        transition: "0.2s"
                                                    }}
                                                >
                                                    <IoIosArrowDown />
                                                </span>
                                            </td>
                                            <td>
                                                <Link
                                                    to={`/measurement-requests/${request._id}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="text-decoration-none"
                                                >
                                                    #{request._id.slice(-8)}
                                                </Link>
                                            </td>
                                            <td>
                                                <span className={typeBadge.class}>
                                                    {typeBadge.text}
                                                </span>
                                            </td>
                                            <td>
                                                <div>
                                                    <strong>{request.requested_by?.user_name}</strong>
                                                    <br />
                                                    <small className="text-muted">
                                                        ({request.requested_by?.user_role})
                                                    </small>
                                                </div>
                                            </td>
                                            <td>
                                                <div>
                                                    {request.client_details?.name || 'N/A'}
                                                    <br />
                                                    <small className="text-muted">
                                                        {request.client_details?.phone || 'N/A'}
                                                    </small>
                                                </div>
                                            </td>
                                            <td>{formatDate(request.preferred_date)}</td>
                                            <td>
                                                <span className={statusBadge.class}>
                                                    {statusBadge.text}
                                                </span>
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(request._id);
                                                    }}
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>

                                        {/* EXPANDED ROW */}
                                        {isOpen && (
                                            <tr>
                                                <td colSpan="8" className="bg-light p-3">
                                                    <div className="row">
                                                        {/* Client Details */}
                                                        <div className="col-md-6 mb-3">
                                                            <h6 className="mb-2">Client Details</h6>
                                                            <table className="table table-sm table-borderless mb-0">
                                                                <tbody>
                                                                    <tr>
                                                                        <td style={{ width: "120px" }}><strong>Name:</strong></td>
                                                                        <td>{request.client_details?.name || 'N/A'}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td><strong>Email:</strong></td>
                                                                        <td>{request.client_details?.email || 'N/A'}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td><strong>Phone:</strong></td>
                                                                        <td>{request.client_details?.phone || 'N/A'}</td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </div>

                                                        {/* Address Details */}
                                                        {request.address_details?.address_line_1 && (
                                                            <div className="col-md-6 mb-3">
                                                                <h6 className="mb-2">Address Details</h6>
                                                                <div className="text-muted small">
                                                                    <div>{request.address_details.address_line_1}</div>
                                                                    {request.address_details.address_line_2 && <div>{request.address_details.address_line_2}</div>}
                                                                    {request.address_details.landmark && <div>Landmark: {request.address_details.landmark}</div>}
                                                                    <div>
                                                                        {request.address_details.city && <span>{request.address_details.city}, </span>}
                                                                        {request.address_details.state && <span>{request.address_details.state}, </span>}
                                                                        {request.address_details.country && <span>{request.address_details.country}</span>}
                                                                        {request.address_details.pincode && <span> - {request.address_details.pincode}</span>}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Schedule Details */}
                                                        <div className="col-md-6 mb-3">
                                                            <h6 className="mb-2">Schedule Details</h6>
                                                            <table className="table table-sm table-borderless mb-0">
                                                                <tbody>
                                                                    <tr>
                                                                        <td style={{ width: "120px" }}><strong>Preferred Date:</strong></td>
                                                                        <td>{formatDate(request.preferred_date)}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td><strong>Preferred Time:</strong></td>
                                                                        <td>{getTimeSlotLabel(request.preferred_time_slot)}</td>
                                                                    </tr>
                                                                    {request.scheduled_date && (
                                                                        <>
                                                                            <tr>
                                                                                <td><strong>Scheduled Date:</strong></td>
                                                                                <td>{formatDate(request.scheduled_date)}</td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td><strong>Scheduled Time:</strong></td>
                                                                                <td>{getTimeSlotLabel(request.scheduled_time_slot)}</td>
                                                                            </tr>
                                                                        </>
                                                                    )}
                                                                </tbody>
                                                            </table>
                                                        </div>

                                                        {/* Additional Info */}
                                                        {request.additional_info && (
                                                            <div className="col-md-6 mb-3">
                                                                <h6 className="mb-2">Additional Information</h6>
                                                                <div className="text-muted small">
                                                                    {request.additional_info}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Admin Notes */}
                                                        {request.admin_notes && (
                                                            <div className="col-12 mb-3">
                                                                <h6 className="mb-2">Admin Notes</h6>
                                                                <div className="text-muted small bg-white p-2 rounded border">
                                                                    {request.admin_notes}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Metadata / Additional Fields */}
                                                        {(request.project_context || request.requested_by) && (
                                                            <div className="col-12">
                                                                <h6 className="mb-2">Additional Information</h6>
                                                                <table className="table table-sm table-bordered mb-0">
                                                                    <thead className="table-light">
                                                                        <tr>
                                                                            <th style={{ width: "30%" }}>Field</th>
                                                                            <th>Value</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {request.project_context?.name && (
                                                                            <tr>
                                                                                <td>Project Name</td>
                                                                                <td>{request.project_context.name}</td>
                                                                            </tr>
                                                                        )}
                                                                        {request.project_context?.board_name && (
                                                                            <tr>
                                                                                <td>Board Name</td>
                                                                                <td>{request.project_context.board_name}</td>
                                                                            </tr>
                                                                        )}
                                                                        {request.cancelled_reason && (
                                                                            <tr>
                                                                                <td>Cancellation Reason</td>
                                                                                <td className="text-danger">{request.cancelled_reason}</td>
                                                                            </tr>
                                                                        )}
                                                                        {request.completed_at && (
                                                                            <tr>
                                                                                <td>Completed At</td>
                                                                                <td>{new Date(request.completed_at).toLocaleString()}</td>
                                                                            </tr>
                                                                        )}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="8" className="text-center text-muted py-5">
                                    No measurement requests found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default MeasurementRequestsPage;